import { useCallback, useEffect, useRef, useState } from 'react';

import type { ProjectListItem, UiMilestone, VerificationProfileSummary } from '@nightshift/ui-types';

import { MarkdownComposer } from './markdown-composer.tsx';
import { Kicker, Spinner } from './primitives.tsx';
import { api, ApiError } from '../live/api.ts';
import { useStore } from '../live/context.tsx';
import {
  applyGeneratedDraft,
  createTicketBody,
  createTicketFormError,
  namedMilestoneError,
  preserveSourceImages,
  hasPendingImages,
} from '../lib/create-ticket.ts';
import { PlusIcon } from '../shell/icons.tsx';

/** Quick mode's own state. `error` keeps the source and offers Retry; it never closes. */
type GenerateState = 'idle' | 'queued' | 'processing' | 'error';

/**
 * Create Ticket. Project and milestone lists come from GET /api/projects and
 * GET /api/milestones — not snapshot, which still 500s on demolished jobs SQL.
 * Source is always the session user (human); there is no agent toggle.
 *
 * Quick Ticket Add organises source notes with a runner. Generate preview opens the editable
 * manual form; Generate and add submits the generated fields directly to Backlog.
 */
export function CreateTicketDialog({
  defaultProject,
  onClose,
  onCreated,
}: {
  defaultProject: string | null;
  onClose: () => void;
  onCreated?: (ticketKey: string) => void;
}) {
  const store = useStore();
  const [projects, setProjects] = useState<ProjectListItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [problem, setProblem] = useState('');
  const [acceptance, setAcceptance] = useState('');
  const [project, setProject] = useState(defaultProject ?? '');
  const [milestones, setMilestones] = useState<UiMilestone[]>([]);
  const [milestoneId, setMilestoneId] = useState<number | null>(null);
  const [verificationProfile, setVerificationProfile] = useState('');
  const [profiles, setProfiles] = useState<VerificationProfileSummary[]>([]);
  const [codedDefault, setCodedDefault] = useState('standard-code');
  const [adding, setAdding] = useState(false);
  const [newMilestone, setNewMilestone] = useState('');
  const [milestoneError, setMilestoneError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'manual' | 'quick'>('manual');
  const [source, setSource] = useState('');
  const [generateState, setGenerateState] = useState<GenerateState>('idle');
  const [generateMessage, setGenerateMessage] = useState<string | null>(null);
  const [generateNote, setGenerateNote] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const addRef = useRef<HTMLInputElement>(null);
  const generationBusyRef = useRef(false);
  const [sourceUploading, setSourceUploading] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  /**
   * Bumped by every action that invalidates an in-flight generation (a new Generate, a switch
   * to manual, a project change, closing). A poll whose sequence is stale returns without
   * touching state, so a late answer can never overwrite a newer one or the user's own edits.
   */
  const generationRef = useRef(0);
  /** The request that is still the server's problem, so it can be abandoned on the way out. */
  const activeRequestRef = useRef<number | null>(null);
  const milestonesRef = useRef<UiMilestone[]>([]);
  const profilesRef = useRef<VerificationProfileSummary[]>([]);
  milestonesRef.current = milestones;
  profilesRef.current = profiles;

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    void api
      .projects()
      .then((listed) => {
        if (cancelled) return;
        setProjects(listed.projects);
        setLoadError(null);
        setProject((current) => {
          if (current && listed.projects.some((entry) => entry.slug === current)) return current;
          return listed.projects[0]?.slug ?? '';
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setProjects([]);
        setLoadError(error instanceof Error ? error.message : String(error));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void api
      .verificationProfiles()
      .then((listed) => {
        if (cancelled) return;
        setProfiles(listed.items);
        setCodedDefault(listed.codedDefault);
      })
      .catch(() => {
        if (cancelled) return;
        setProfiles([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!project) {
      setMilestones([]);
      setMilestoneId(null);
      return;
    }
    let cancelled = false;
    void api
      .milestones(project)
      .then((listed) => {
        if (cancelled) return;
        setMilestones(listed.milestones);
        setMilestoneId((current) =>
          current != null && listed.milestones.some((entry) => entry.id === current)
            ? current
            : null,
        );
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof ApiError && error.status === 404) {
          setMilestones([]);
          setMilestoneId(null);
          return;
        }
        store.toast('bad', error instanceof Error ? error.message : String(error));
      });
    return () => {
      cancelled = true;
    };
  }, [project, store]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      event.preventDefault();
      if (!busy) onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose, busy]);

  useEffect(() => {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const onKey = (event: KeyboardEvent) => {
      const dialog = dialogRef.current;
      if (!dialog || !dialog.contains(event.target as Node) || event.key !== 'Tab') return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      if (openerRef.current?.isConnected) openerRef.current.focus();
    };
  }, []);

  useEffect(() => {
    if (adding) addRef.current?.focus();
  }, [adding]);

  useEffect(() => {
    if (mode === 'quick') document.getElementById('ticket-quick-source')?.focus();
  }, [mode]);

  /**
   * Stop caring about the current request, and tell the server so.
   *
   * Abandonment is best effort on purpose: the sequence bump is what makes the answer
   * unusable to this dialog, and the DELETE only saves the runner some work. A failed DELETE
   * therefore cannot leave a ticket behind, because no path from a draft request writes one.
   */
  const abandonGeneration = useCallback(() => {
    generationRef.current += 1;
    generationBusyRef.current = false;
    const id = activeRequestRef.current;
    activeRequestRef.current = null;
    if (id !== null) void api.cancelTicketDraft(id).catch(() => undefined);
    setGenerateState('idle');
    setGenerateMessage(null);
  }, []);

  // Closing the dialog by any route abandons an in-flight generation. Unmounting alone would
  // already make a late answer harmless; this stops the runner spending on it as well.
  useEffect(() => () => {
    generationRef.current += 1;
    generationBusyRef.current = false;
    const id = activeRequestRef.current;
    activeRequestRef.current = null;
    if (id !== null) void api.cancelTicketDraft(id).catch(() => undefined);
  }, []);

  const generate = async (direct = false): Promise<void> => {
    if (generationBusyRef.current || sourceUploading || hasPendingImages(source)) return;
    generationBusyRef.current = true;
    const submittedProject = project;
    const submittedSource = source;
    if (submittedSource.trim().length === 0 || !submittedProject) {
      generationBusyRef.current = false;
      return;
    }
    const sequence = generationRef.current + 1;
    generationRef.current = sequence;
    setGenerateState('queued');
    setGenerateMessage('Queued for a runner…');
    setGenerateNote(null);
    try {
      const created = await api.createTicketDraft({ project: submittedProject, source: submittedSource });
      if (generationRef.current !== sequence) {
        void api.cancelTicketDraft(created.requestId).catch(() => undefined);
        return;
      }
      activeRequestRef.current = created.requestId;
      for (;;) {
        await new Promise((resolve) => window.setTimeout(resolve, 750));
        if (generationRef.current !== sequence) return;
        const status = await api.ticketDraft(created.requestId);
        if (generationRef.current !== sequence) return;
        if (status.status === 'queued' || status.status === 'processing') {
          setGenerateState(status.status);
          setGenerateMessage(
            status.status === 'queued'
              ? 'Queued for a runner…'
              : 'A runner is organising these notes into a ticket…',
          );
          continue;
        }
        activeRequestRef.current = null;
        if (status.status !== 'succeeded' || !status.result) {
          setGenerateState('error');
          setGenerateMessage(
            status.error ?? 'The draft did not come back. Your notes are still here — retry, or enter the ticket manually.',
          );
          return;
        }
        const next = applyGeneratedDraft(
          { project: submittedProject, title, problem, acceptanceCriteria: acceptance, milestoneId, verificationProfile },
          preserveSourceImages(status.result, submittedSource),
          {
            milestoneIds: milestonesRef.current.map((entry) => entry.id),
            profileNames: profilesRef.current.map((entry) => entry.name),
          },
        );
        if (direct) {
          const error = createTicketFormError(next, projects);
          if (error) throw new Error(error);
          setBusy(true);
          try {
            const result = await api.createTicket(createTicketBody(next));
            store.toast('good', result.message);
            onClose();
            if (result.ticketKey) onCreated?.(result.ticketKey);
            void store.refetch().catch(() => undefined);
          } finally {
            setBusy(false);
          }
          return;
        }
        setTitle(next.title);
        setProblem(next.problem);
        setAcceptance(next.acceptanceCriteria);
        setMilestoneId(next.milestoneId);
        setVerificationProfile(next.verificationProfile);
        setGenerateState('idle');
        setGenerateMessage(null);
        setGenerateNote(
          [
            'Draft generated and not saved. Review and edit anything below, then Add to backlog.',
            ...(status.warnings ?? []),
          ].join(' '),
        );
        setMode('manual');
        return;
      }
    } catch (error) {
      if (generationRef.current !== sequence) return;
      activeRequestRef.current = null;
      setGenerateState('error');
      setGenerateMessage(
        `${error instanceof Error ? error.message : String(error)} Your notes are still here — retry, or enter the ticket manually.`,
      );
    } finally {
      if (generationRef.current === sequence) generationBusyRef.current = false;
    }
  };

  const formError = createTicketFormError({ project, title }, projects);
  const ready = projects !== null;
  const generating = generateState === 'queued' || generateState === 'processing';

  const addMilestone = async (): Promise<void> => {
    const error = namedMilestoneError(newMilestone);
    if (error) {
      setMilestoneError(error);
      return;
    }
    if (!project || busy) return;
    setBusy(true);
    setMilestoneError(null);
    try {
      const result = await api.createMilestone({ project, name: newMilestone.trim() });
      setMilestones((current) => {
        if (current.some((entry) => entry.id === result.milestone.id)) return current;
        return [...current, result.milestone].sort((a, b) => a.name.localeCompare(b.name));
      });
      setMilestoneId(result.milestone.id);
      setNewMilestone('');
      setAdding(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setMilestoneError(message);
      store.toast('bad', message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async (): Promise<void> => {
    if (mode !== 'manual' || formError || busy || !ready || hasPendingImages(problem + acceptance)) return;
    setBusy(true);
    try {
      const result = await api.createTicket(
        createTicketBody({
          project,
          title,
          problem,
          acceptanceCriteria: acceptance,
          milestoneId,
          verificationProfile,
        }),
      );
      store.toast('good', result.message);
      await store.refetch();
      onClose();
      if (result.ticketKey) onCreated?.(result.ticketKey);
    } catch (error) {
      store.toast('bad', error instanceof Error ? error.message : String(error));
      setBusy(false);
    }
  };

  return (
    <>
      <div className="drawer-scrim" onClick={() => { if (!busy) onClose(); }} role="presentation" />
      <div
        ref={dialogRef}
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Create Ticket"
        data-ready={ready ? '1' : '0'}
      >
        <header className="dialog-head">
          <Kicker>Create ticket</Kicker>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => { if (!busy) onClose(); }}>
            Esc
          </button>
        </header>

        <form
          className="dialog-body"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          {loadError ? <p className="dialog-error">{loadError}</p> : null}
          {projects !== null && projects.length === 0 && !loadError ? (
            <p className="dialog-error">No projects registered.</p>
          ) : null}

          <div className="dialog-modes" role="tablist" aria-label="Ticket entry mode">
            <button
              type="button"
              role="tab"
              disabled={busy || sourceUploading}
              className="dialog-mode"
              aria-selected={mode === 'manual'}
              onClick={() => {
                // Returning to manual entry is always available, including mid-generation.
                if (generating) abandonGeneration();
                setMode('manual');
              }}
            >
              Manual
            </button>
            <button
              type="button"
              role="tab"
              disabled={busy || sourceUploading}
              className="dialog-mode"
              aria-selected={mode === 'quick'}
              onClick={() => setMode('quick')}
            >
              Quick Ticket Add
            </button>
          </div>

          <label className="dialog-field">
            <span className="dialog-label">Project</span>
            <select
              className="input"
              name="project"
              value={project}
              disabled={busy || sourceUploading || !ready || (projects?.length ?? 0) === 0}
              onChange={(event) => {
                // A draft is generated against one project's milestones and ticket types.
                // Moving to another project abandons it rather than carrying a choice across.
                abandonGeneration();
                setMilestoneId(null);
                setVerificationProfile('');
                setGenerateNote(null);
                setProject(event.target.value);
              }}
            >
              {projects === null ? <option value={project}>{project || 'Loading…'}</option> : null}
              {(projects ?? []).map((entry) => (
                <option key={entry.slug} value={entry.slug}>
                  {entry.name} ({entry.key})
                </option>
              ))}
            </select>
          </label>

          {mode === 'quick' ? (
            <>
              <label className="dialog-field">
                <span className="dialog-label">
                  Notes to organise
                  <span className="dim"> — paste or dictate</span>
                </span>
                <MarkdownComposer
                  project={project || null}
                  onUploadingChange={setSourceUploading}
                  id="ticket-quick-source"
                  rows={12}
                  value={source}
                  disabled={generating}
                  placeholder="Paste or dictate everything you know about this ticket. One block is fine — Nightshift splits it into title, Problem and acceptance criteria."
                  onChange={setSource}
                />
              </label>
              {generateMessage ? (
                <p
                  className={generateState === 'error' ? 'dialog-error' : 'dim'}
                  role={generateState === 'error' ? 'alert' : 'status'}
                  style={generateState === 'error' ? undefined : { fontSize: 'var(--fs-tiny)' }}
                >
                  {generateMessage}
                </p>
              ) : null}
              <p className="dim" style={{ fontSize: 'var(--fs-tiny)', margin: 0 }}>
                Generate preview opens an editable draft. Generate and add saves it directly to Backlog.
              </p>
            </>
          ) : null}

          {/* The normal editable form. Identical in both modes: a generated draft is
              folded into this same state, so review and manual entry are one screen. */}
          {mode === 'manual' ? (
            <>
              {generateNote ? (
                <p className="dialog-draft-note" role="status">
                  {generateNote}
                </p>
              ) : null}
            <div className="dialog-split">
              <div className="dialog-field">
                <span className="dialog-label">Milestone</span>
                <div className="dialog-milestone-row">
                  <select
                    className="input"
                    name="milestone"
                    value={milestoneId == null ? '' : String(milestoneId)}
                    disabled={!project}
                    onChange={(event) => {
                      const raw = event.target.value;
                      setMilestoneId(raw.length === 0 ? null : Number(raw));
                    }}
                  >
                    <option value="">None</option>
                    {milestones.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-sm"
                    aria-label="Add milestone"
                    disabled={!project || busy}
                    onClick={() => {
                      setAdding(true);
                      setMilestoneError(null);
                    }}
                  >
                    <PlusIcon size={12} />
                  </button>
                </div>
              </div>

              <label className="dialog-field">
                <span className="dialog-label">Ticket type</span>
                <select
                  className="input"
                  name="verificationProfile"
                  value={verificationProfile}
                  onChange={(event) => setVerificationProfile(event.target.value)}
                >
                  <option value="">Auto (Project Default) - {codedDefault}</option>
                  {(profiles.length > 0
                    ? profiles
                    : [
                        { name: 'standard-code' },
                        { name: 'visual-change' },
                        { name: 'gameplay-change' },
                        { name: 'performance-change' },
                      ]
                  ).map((entry) => (
                    <option key={entry.name} value={entry.name}>
                      {entry.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {adding ? (
              <div className="dialog-milestone-add">
                <input
                  ref={addRef}
                  className="input"
                  value={newMilestone}
                  maxLength={200}
                  placeholder="Milestone name"
                  onChange={(event) => setNewMilestone(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void addMilestone();
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={busy}
                  onClick={() => void addMilestone()}
                >
                  Add
                </button>
              </div>
            ) : null}
            {milestoneError ? <p className="dialog-error">{milestoneError}</p> : null}

            <label className="dialog-field">
              <span className="dialog-label">Title</span>
              <input
                ref={titleRef}
                className="input"
                name="title"
                value={title}
                maxLength={200}
                  placeholder="Summarise the work item in one sentence"
                onChange={(event) => setTitle(event.target.value)}
              />
            </label>

            <label className="dialog-field">
              <span className="dialog-label">
                Problem
                <span className="dim"> — optional</span>
              </span>
              <MarkdownComposer
                initialMode={generateNote ? 'preview' : 'write'}
                id="ticket-problem"
                value={problem}
                rows={4}
                placeholder="What needs doing."
                disabled={busy}
                project={project || null}
                enableMentions
                onChange={setProblem}
              />
            </label>

            <label className="dialog-field">
              <span className="dialog-label">
                Acceptance criteria
                <span className="dim"> — optional</span>
              </span>
              <MarkdownComposer
                initialMode={generateNote ? 'preview' : 'write'}
                id="ticket-acceptance"
                value={acceptance}
                rows={3}
                placeholder="Rough is allowed."
                disabled={busy}
                project={project || null}
                enableMentions
                onChange={setAcceptance}
              />
            </label>
            </>
          ) : null}

          <footer className="dialog-foot">
            <span className="dim" style={{ fontSize: 'var(--fs-tiny)', flex: 1 }}>
              {mode === 'quick'
                ? 'Choose to review the draft or add it directly to Backlog.'
                : 'Lands in Backlog. Start it from the card when you are ready.'}
            </span>
            {mode === 'quick' ? (
              <>
                {/* Manual entry stays one click away at every point, including mid-generation
                    and after a failure — the notes are kept either way. */}
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={busy || sourceUploading}
                  onClick={() => {
                    if (generating) abandonGeneration();
                    setMode('manual');
                  }}
                >
                  Enter manually
                </button>
                <button
                  type="button"
                  className="btn btn-accent btn-sm"
                  disabled={!ready || !project || source.trim().length === 0 || generating || sourceUploading || hasPendingImages(source)}
                  onClick={() => void generate()}
                >
                  {generating ? <Spinner /> : null}{' '}
                  Generate preview
                </button>
                <button
                  type="button"
                  className="btn btn-accent btn-sm"
                  disabled={!ready || !project || !source.trim() || generating || sourceUploading || hasPendingImages(source)}
                  onClick={() => void generate(true)}
                >
                  Generate and add
                </button>
              </>
            ) : (
              <button
                type="submit"
                className="btn btn-accent btn-sm"
                disabled={busy || formError !== null || !ready || hasPendingImages(problem + acceptance)}
              >
                Add to backlog
              </button>
            )}
          </footer>
        </form>
      </div>
    </>
  );
}
